import { useMutation } from 'react-query';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';
import {
  useCareCreditCreatePaymentIntent,
  CareCreditCreatePaymentIntentOptions,
} from './use-care-credit-create-payment-intent';

export type UseCareCreditMakePaymentMutationProps = CareCreditCreatePaymentIntentOptions;

export const useCareCreditMakePaymentMutation = (props: UseCareCreditMakePaymentMutationProps) => {
  const { locationId } = props;

  const { isLocationLoaded, isPaymentIdLoaded } = useConfirmPaymentHelpers();

  const { createPaymentIntent } = useCareCreditCreatePaymentIntent(props);

  const { mutateAsync: makePaymentMutation, isLoading: makingPayment } = useMutation({
    mutationFn: async () => {
      if (!isLocationLoaded(locationId)) {
        // alerts own errors
        return;
      }

      const { paymentId, status } = await createPaymentIntent();

      if (!isPaymentIdLoaded(paymentId)) {
        // alerts own errors
        return;
      }

      return { paymentId, status };
    },
  });

  return {
    makePaymentMutation,
    makingPayment,
  };
};

const useConfirmPaymentHelpers = () => {
  const alerts = useAlert();
  const { t } = useTranslation('payments');

  const errorAlert = (message: string) =>
    alerts.error({
      autoDismissAfter: 12000,
      message,
    });

  const isLocationLoaded = (locationId: string | undefined): locationId is string => {
    if (!locationId) {
      errorAlert(t('Location ID is required.'));
      return false;
    }
    return true;
  };

  const isPaymentIdLoaded = (paymentId: string | undefined): paymentId is string => {
    if (!paymentId) {
      errorAlert(t('Failed to create payment intent. Please try again.'));
      return false;
    }
    return true;
  };

  return {
    errorAlert,
    isPaymentIdLoaded,
    isLocationLoaded,
  };
};
